import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import Modal from './SemestresModal';
import Tabs from './SemestresTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Semestres";
const endpoint = ENDPOINTS.semestres;
const endpointById = (id) => ENDPOINTS.semestresById(id);
const modelo = Models.semestre;

export default function SemestresCatalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        if (!endpoint) return;
        setLoading(true);
        try {
            const [semRes, carrRes] = await Promise.all([
                axios.get(endpoint),
                axios.get(ENDPOINTS.carreras)
            ]);
            setRows((semRes.data || []).map((x) => modelo ? modelo(x) : x));
            setCarreras((carrRes.data || []).map(x => Models.carrera(x)));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const getCarreraNombre = (id) => {
        const c = carreras.find(x => x.idCarrera === id);
        return c ? normalizeValue(c.nombre) : 'N/A';
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        
        // 1. Filtrar solo semestres ACTIVOS que pertenezcan a carreras ACTIVADAS
        const activeOnly = rows.filter(r => {
            if (!r.activo) return false;
            const c = carreras.find(x => x.idCarrera === r.idCarrera);
            return c && c.activado === true; // En carreras la propiedad es 'activado'
        });

        if (!q) return activeOnly;
        return activeOnly.filter((r) => {
            const nombre = (r.nombre || '').toLowerCase();
            const c = carreras.find(x => x.idCarrera === r.idCarrera);
            const carreraNom = c ? normalizeValue(c.nombre).toLowerCase() : 'n/a';
            return nombre.includes(q) || carreraNom.includes(q);
        });
    }, [rows, query, carreras]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedId(item.idSemestre);
        setEditData(item);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar. Verifique que el semestre no tenga registros asociados.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            const payload = {
                IdSemestre: mode === 'new' ? 0 : selectedId,
                IdCarrera: form.idCarrera,
                Nombre: form.nombre,
                IdSemestreAnterior: form.idSemestreAnterior || null,
                Activo: form.activo
            };

            if (mode === 'new') {
                await axios.post(endpoint, payload);
            } else {
                await axios.put(endpoint, payload);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por nombre o carrera…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Carrera</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idSemestre}>
                                        <td><strong>{normalizeValue(r.nombre)}</strong></td>
                                        <td>{getCarreraNombre(r.idCarrera)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--edit"
                                                onClick={() => handleEdit(r)}
                                                aria-label="Editar"
                                                title="Editar"
                                            >
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--del"
                                                onClick={() => askDelete(r.idSemestre, r.nombre)}
                                                aria-label="Borrar"
                                                title="Borrar"
                                            >
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1rem' }}>Sin resultados encontrados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        {modalMode === 'new' && (
                            <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                                <span>
                                    Selecciona o crea un semestre y pulsa <b>Editar</b> para gestionar sus datos relacionados.
                                </span>
                            </div>
                        )}
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                        {modalMode === 'edit' && selectedId > 0 && (
                            <Tabs semestreId={selectedId} />
                        )}
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
